import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { combineLatest, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { BankAccountsService } from '../../../../services/financial-accounts/bank-accounts.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UnifiedBankAccount } from '../../../../models/entities/unified-bank-account';
import { AddOrgBankAccountDialogComponent, AddOrgBankAccountDialogInput, AddOrgBankAccountDialogResult } from '../bank-account-manager/add-org-bank-account-dialog/add-org-bank-account-dialog.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput } from '../confirm-action-dialog/confirm-action-dialog.component';
import { FinancialConnectionsService } from '../../../../services/stripe-treasury/financial-connections.service';
import { TreasuryService } from '../../../../services/stripe-treasury/treasury.service';
import { ONBOARDING_STATUSES } from '../../../v2-layout/stripe-treasury/stripe-treasury.constants';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { DepositBankAccount } from '../../../../models/entities/deposit-bank-account';
import { MatLegacySelect as MatSelect, MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { SharedModule } from '../../../shared/shared.module';
import { LFDropdownModule } from '../../../shared/dropdown/dropdown.module';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';

@UntilDestroy()
@Component({
    selector: 'app-deposit-account-selector',
    styleUrls: ['./deposit-account-selector.scss'],
    templateUrl: './deposit-account-selector.component.html',
    standalone: true,
    imports: [
        // Shared
        SharedModule,
        // Dependencies

        LFDropdownModule,
        MatDialogModule,
        MatFormFieldModule,
        MatOptionModule,
        MatSelectModule,
        MatProgressSpinnerModule,
    ],
})
export class DepositAccountSelectorComponent implements OnInit
{
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName: string = 'account';
    @Input() organizationId: number = null;
    @Input() allowCreate = true;

    @Output() treasuryAccountDetected = new EventEmitter<DepositBankAccount>();
    @Output() loaded = new EventEmitter<void>();

    @ViewChild(MatSelect) selectRef: MatSelect;

    isLoadingAccounts = true;
    isTreasuryLocked = false;
    treasuryAccountName = '';
    provisioningBlockedMessage: string | null = null;
    private _slowProvisioningDialogShown = false;
    private _treasuryAccountEmitted = false;

    bankAccounts: DepositBankAccount[] = [];

    /**
     * Window after which we tell the user their Treasury account is taking longer
     * than usual to set up. Matches the post-KYC polling window in
     * `FinancialConnectionsService._pollUntilTreasuryAccountReady` so the hint
     * appears around the same time polling gives up.
     */
    private static readonly TREASURY_PROVISIONING_SLOW_MS = 3 * 60 * 1000;

    private _addingBankAccount = false;

    constructor(
            private _bankAccountsService: BankAccountsService,
            private _dialog: MatDialog,
            private _financialConnectionsService: FinancialConnectionsService,
            private _treasuryService: TreasuryService,
    ) {
    }

    ngOnInit() {
        if (!this.organizationId) {
            const jwt = LegFiJwtService.read();
            if (jwt !== null) {
                this.organizationId = jwt.orgId;
            }
        }
        this.loadBankAccounts();
        this._lockOnTreasuryVerifiedSignal();
    }

    /**
     * SD-6766: react to the connect-status signal directly, not just bank-accounts data.
     *
     * The Treasury DBA is created asynchronously by `treasury.financial_account.created`,
     * which lags `account.updated` (the status-flip event) by 1-3 minutes. The Treasury
     * dashboard already gates its rendering on `connectStatus$.pipe(map(s => s === VERIFIED))`
     * — that's why it flips instantly post-KYC. This selector previously waited for the
     * data (bank-accounts emission with `isTreasuryAccount=true`), which made the user
     * stare at the "Select Bank Account" dropdown for the duration of the DBA-creation lag.
     *
     * Mirror the dashboard pattern: as soon as status confirms `verified`, flip the
     * selector to the readonly "PayHOA Balance" presentation. The form control stays
     * without a value (and thus the form stays invalid for submit) until the
     * bank-accounts subscription picks up the real DBA, so submits remain correctly
     * gated on the DBA actually existing.
     */
    private _lockOnTreasuryVerifiedSignal(): void {
        this._treasuryService.connectStatus$.pipe(
            filter((status) => status === ONBOARDING_STATUSES.VERIFIED),
            take(1),
            untilDestroyed(this),
        ).subscribe(() => {
            if (this.isTreasuryLocked) {
                return; // bank-accounts stream already locked us via the data path
            }
            this.isTreasuryLocked = true;
            this.treasuryAccountName = 'PayHOA Balance';
            this.formGroup.get(this.controlName)?.disable();
            this._startSlowProvisioningTimer();
        });
    }

    /**
     * SD-6766 follow-on UX (SD-6865 AC #3): if the real Treasury DBA doesn't
     * materialize within the broadcast window, the inline form treatment is
     * too easy to miss. Pop a one-shot modal alert so the user knows setup is
     * still in progress and can refresh on demand. Guarded by
     * `_slowProvisioningDialogShown` so dismissal is sticky — we don't re-nag
     * if the same selector instance hits the timer again.
     */
    private _startSlowProvisioningTimer(): void {
        timer(DepositAccountSelectorComponent.TREASURY_PROVISIONING_SLOW_MS).pipe(
            take(1),
            untilDestroyed(this),
        ).subscribe(() => {
            const hasResolvedDba = !!this.formGroup.get(this.controlName)?.value;
            if (!hasResolvedDba && !this._slowProvisioningDialogShown) {
                this._openSlowProvisioningDialog();
            }
        });
    }

    private _openSlowProvisioningDialog(): void {
        this._slowProvisioningDialogShown = true;
        // SD-6884: belt-and-suspenders auto-retry before showing the user the
        // "Refresh now" CTA. If the broadcast was missed (Echo disconnect,
        // delayed Stripe webhook, etc.) the broadcasts may never have reached
        // this session, so fire a single connect-status refresh that may
        // resolve the stale state without requiring the user to click anything.
        // If the refresh re-emits a verified status, the locked-on-verified
        // signal subscriber will lock the selector and the dialog can be
        // dismissed without action.
        this._treasuryService.refreshConnectStatus(this.organizationId);
        const data: ConfirmActionDialogInput = {
            title: 'Account Provisioning',
            content: 'Setting up your PayHOA Balance is taking longer than usual. Refresh the page in a few minutes to pick up the latest status.',
            confirmButton: 'Refresh now',
            cancelButton: 'Dismiss',
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            width: ConfirmActionDialogComponent.width,
            data,
        }).afterClosed().pipe(untilDestroyed(this)).subscribe((result) => {
            if (result?.success) {
                window.location.reload();
            }
        });
    }

    loadBankAccounts() {
        const bankAccountStreams = this._bankAccountsService.getOrganizationStreams(this.organizationId);

        if (!bankAccountStreams.$loaded.getValue()) {
            this._bankAccountsService.getOrgBankAccounts(this.organizationId, null, true).pipe(untilDestroyed(this)).subscribe();
        }

        this.isLoadingAccounts = true;
        // Pair $bankAccounts with $loaded so the spinner clears even when the
        // API returns an empty array. BankAccountsService publishes
        // $bankAccounts.next([]) BEFORE $loaded.next(true), so subscribing to
        // $bankAccounts alone produces a stuck spinner for fresh orgs: the
        // initial emission sees $loaded=false and the prior early-return guard
        // suppressed it, then the subsequent $loaded flip never re-triggers
        // the bank-accounts stream. combineLatest re-emits whenever either
        // input changes, and the filter on `loaded` guarantees we only act
        // once the load actually completes — regardless of whether the org
        // has any bank accounts.
        combineLatest([
            bankAccountStreams.$bankAccounts,
            bankAccountStreams.$loaded,
        ]).pipe(
            filter(([_, loaded]) => loaded),
            untilDestroyed(this),
        ).subscribe({
            next: ([accounts]) => {
                this.bankAccounts = accounts.filter((uba) => {
                    return uba.depositBankAccount;
                }).map((uba) => uba.depositBankAccount);
                this.isLoadingAccounts = false;

                if (accounts.length === 0) {
                    return;
                }

                const treasuryUba = accounts.find(a => a.isTreasuryAccount);
                if (treasuryUba?.depositBankAccount) {
                    const control = this.formGroup.get(this.controlName);
                    control?.setValue(treasuryUba.depositBankAccount);
                    control?.disable();
                    this.isTreasuryLocked = true;
                    this.treasuryAccountName = treasuryUba.depositBankAccount.friendlyName;
                    // Clear any provisioning-blocked message when the DBA finally
                    // lands — including the case where it arrives AFTER the 3-min
                    // slow-provisioning dialog fired (server-cache TTL fallback
                    // from SD-6828). The slow-provisioning dialog is one-shot
                    // and self-dismissing, so no extra cleanup needed there.
                    this.provisioningBlockedMessage = null;
                    // SD-6865 broadcast adoption: combineLatest re-emits on every
                    // $bankAccounts push (and the DBA-ready broadcast triggers a
                    // refresh on the same stream), so this branch can run
                    // repeatedly with the same treasuryUba. Emit the detected
                    // event only on the first transition so parent consumers do
                    // not need to dedupe duplicate same-DBA emissions.
                    if (!this._treasuryAccountEmitted) {
                        this._treasuryAccountEmitted = true;
                        this.treasuryAccountDetected.emit(treasuryUba.depositBankAccount);
                    }
                } else {
                    this.loaded.emit();
                }
            },
        });
    }

    /**
     * True when the selector has flipped into the readonly "PayHOA Balance"
     * presentation via the connect-status signal (`_lockOnTreasuryVerifiedSignal`)
     * but the bank-accounts stream hasn't yet delivered the real Treasury DBA, so
     * the form control is disabled-and-null. Callers (e.g. the charge form) must
     * gate their submit on this so they don't deref a null `depositBankAccount`
     * during the post-KYC DBA-provisioning window.
     */
    isAwaitingTreasuryDba(): boolean {
        return this.isTreasuryLocked
                && !this.formGroup.get(this.controlName)?.value;
    }

    /**
     * Surface the inline "still being set up" message using the same
     * `form-error-message text-danger` markup that legacy `lf-control-messages`
     * emits. Parents call this when they detect a submit attempt during the
     * post-KYC DBA-provisioning window (see `isAwaitingTreasuryDba`). The
     * message auto-clears once the real DBA lands in the bank-accounts stream,
     * so callers do not need to clear it themselves.
     *
     * Note: we render this message ourselves (instead of routing through the
     * existing `lf-control-messages` consumer in the parent) because the form
     * control has `Validators.required` and the validator re-runs after any
     * `setErrors` override, clobbering our custom error key with `required: true`.
     */
    flagProvisioningBlocked(): void {
        this.provisioningBlockedMessage = '* Your PayHOA Balance is still being set up. Try again in a minute.';
    }

    addBankAccount() {
        if (this._addingBankAccount) {
            return;
        }
        this._addingBankAccount = true;

        this._financialConnectionsService.addBankAccount(
                this.organizationId,
                // onStandardDialog fires only for non-Treasury orgs; Treasury orgs route through service-managed KYC/FC flows.
                () => {
                    const data: AddOrgBankAccountDialogInput = {
                        onlyDepositAccounts: true,
                    };
                    this._dialog.open(AddOrgBankAccountDialogComponent, {
                        width: MatDialogSizes.MD,
                        data,
                    }).afterClosed().subscribe({
                        next: (result: AddOrgBankAccountDialogResult) => {
                            this._addingBankAccount = false;
                            if (result?.success) {
                                this.loadBankAccounts();
                            }
                        },
                    });
                },
                // Post-KYC success callback. Live updates to `connectStatus$`
                // and the bank-accounts stream now arrive via the
                // `.treasury.connect-status.updated` and
                // `.treasury.financial-account.ready` broadcasts subscribed in
                // `TreasuryService` and `FinancialConnectionsService` (SD-6865
                // broadcast adoption). This dialog-lifecycle callback just
                // re-loads the bank-accounts stream as a belt-and-suspenders
                // fallback for the lifecycle gap between modal close and the
                // first broadcast landing.
                () => this.loadBankAccounts(),
                () => { this._addingBankAccount = false; },
        );
    }

    comparator(o1: DepositBankAccount, o2: DepositBankAccount) {
        return o1 && o2 && o1.id === o2.id;
    }

    trackByDepositAccountId(_index: number, account: DepositBankAccount): number {
        return account.id;
    }
}
